import { type FunctionComponent, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Code } from '@atlaskit/code';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';

import { useInfo, useToken } from '../../../Flow/ConsentContextProvider';

import type { ScopeInfo } from '#definitions/consent-info';

const headers = defineMessages({
  header: {
    id: 'oauth.bitbucket.permissions.header',
    defaultMessage: '{appName} is requesting access to the following:',
    description: 'Header describing permissions',
  },
});

const permissionsListContainerStyle = xcss({
  marginBlockStart: 'space.150',
});

const groupPermissionsListStyle = xcss({
  marginBlockStart: 'space.050',
});

const otherPermissionsListStyle = xcss({
  paddingInlineStart: 'space.200',
});

const PermissionsBullets: FunctionComponent = () => {
  const { scopes, clientName } = useToken();
  const { client } = useInfo();
  const { appContactLink, scopes: scopeInfo } = client ?? {};

  const permissionsList = useMemo(() => {
    if (!scopeInfo || scopeInfo.length === 0) {
      return scopes.map((scope) => (
        <Box as={'li'} key={scope}>
          <Code>{scope}</Code>
        </Box>
      ));
    }

    // Group scopes by their group name, which can be undefined
    const scopeInfoByGroup: Map<string | undefined, ScopeInfo[]> =
      scopeInfo.reduce((acc, val) => {
        const groupScopeInfos = acc.get(val.groupName);
        if (!groupScopeInfos) {
          acc.set(val.groupName, [val]);
        } else {
          groupScopeInfos.push(val);
        }

        return acc;
      }, new Map<string | undefined, ScopeInfo[]>());

    return scopeInfoByGroup
      .entries()
      .map(([groupName, groupScopeInfos]) => {
        return groupName ? (
          <Stack key={groupName}>
            <Heading size="xsmall">{groupName}</Heading>
            <Box as="ul" xcss={groupPermissionsListStyle}>
              {groupScopeInfos.map((scope) => (
                <Box as={'li'} key={scope.name}>
                  <Text>{scope.scopeDescription}</Text>
                </Box>
              ))}
            </Box>
          </Stack>
        ) : (
          // Scopes without a group name get grouped together
          <Box as="ul" xcss={otherPermissionsListStyle} key="ungrouped">
            {groupScopeInfos.map((scope) => (
              <Box as={'li'} key={scope.name}>
                <Text>{scope.scopeDescription}</Text>
              </Box>
            ))}
          </Box>
        );
      })
      .toArray();
  }, [scopes, scopeInfo]);

  return (
    <Box>
      <FormattedMessage
        {...headers.header}
        values={{
          appName: appContactLink ? (
            <Link href={appContactLink} target="_blank">
              {clientName}
            </Link>
          ) : (
            <strong> {clientName} </strong>
          ),
        }}
      />
      <Stack space="space.050" xcss={permissionsListContainerStyle}>
        {permissionsList}
      </Stack>
    </Box>
  );
};

export default PermissionsBullets;
